// Note this should be moved to the lib
import { BrandInterface } from "../Brand/Brand.Interface";
import { LocationInterface, LocationWithVariablesInterface } from "../Locations/Location.Interface";
import { LocationVarKey } from "../Variables/Generated/LocationVarKey.Data";

export interface SpotDataHeaderSection {
  id: string;
  title?: string;
  content?: string;
  center?: boolean;
  isLink?: boolean;
  href?: string;
  target?: string;
}

export enum SpotDataButtonStyle {
  PRIMARY = "PRIMARY",
  ACCENT = "ACCENT"
}

export interface SpotDataButton {
  id: string;
  icon?: string;
  title?: string;
  href?: string;
  target?: string;
  style: SpotDataButtonStyle;
}

export interface SpotColorInfo {
  bgColor?: string;
  textColor?: string;
  hoverBgColor?: string;
  hoverTextColor?: string;
}

// export interface SpotListingItem {
//   id: number;
//   heading?: string;
//   subHeading?: string;
//   text?: string;
//   imageUri?: string;
//   locationSlug?: string;
// }
export type SpotListingItem = {
  location: LocationInterface;
  variables: {
    [LocationVarKey.Name]: string;
    [LocationVarKey.SpotLocationSlug]: string;
    [LocationVarKey.Address]: string;
    [LocationVarKey.City]: string;
    [LocationVarKey.State]: string;
    [LocationVarKey.PostalCode]: string;
    [LocationVarKey.CountryCode]: string;
    [LocationVarKey.AvatarURL]: string;
    [LocationVarKey.Email]: string;
    [LocationVarKey.Phone]: string;
  };
};

// export interface SpotBrandLoaderData {
//   heading: string;
//   subHeading: string;
//   brand: BrandInterface;
//   locations: SpotListingItem[] | V3SpotListingItem[];
//   version?: number;
// }
export interface SpotData {
  heading?: string;
  subHeading?: string;
  avatarUri?: string;
  backgroundUri?: string;
  slug?: string;
  orgId?: number;
  brand: BrandInterface;
  metadata?: SpotMetadata;
  headerSections?: SpotDataHeaderSection[][];
  buttons?: SpotDataButton[];
  locations?: SpotListingItem[];
  brandLinkSlug?: string;
  locationLinkSlug?: string;
  shortLink?: string;
  version?: number;
  /**
   * True when the brand's org has the Snowy feature flag on (ENG-2893). Spot
   * checks this FIRST, then tries `brand.colors`, then falls back to the legacy
   * `brand.theme` — an org can turn Snowy off during rollout, so `colors` being
   * populated is never a signal on its own.
   */
  snowyEnabled?: boolean;
}

export interface SpotLocationData extends SpotData {
  buttons?: SpotDataButton[];
  locationId?: number;
  reviewURL?: string;
  reviewPageTitle?: string;
  reviewDisclaimer?: string;
  reviewDisclaimerEnabled?: string;
  // brandSlug?: string;
}

interface SpotMetadata {
  /**
   * The base path and origin for absolute urls for various metadata links such as OpenGraph images.
   */
  metadataBase?: null | URL;
  /**
   * The document title.
   * @example
   * ```tsx
   * "My Blog"
   * <title>My Blog</title>
   *
   * { default: "Dashboard", template: "%s | My Website" }
   * <title>Dashboard | My Website</title>
   *
   * { absolute: "My Blog", template: "%s | My Website" }
   * <title>My Blog</title>
   * ```
   */
  title?: null | string;
  /**
   * The document description, and optionally the OpenGraph and twitter descriptions.
   * @example
   * ```tsx
   * "My Blog Description"
   * <meta name="description" content="My Blog Description" />
   * ```
   */
  description?: null | string;
  /**
   * The application name.
   * @example
   * ```tsx
   * "My Blog"
   * <meta name="application-name" content="My Blog" />
   * ```
   */
  applicationName?: null | string;
  /**
   * The authors of the document.
   * @example
   * ```tsx
   * [{ name: "Next.js Team", url: "https://nextjs.org" }]
   *
   * <meta name="author" content="Next.js Team" />
   * <link rel="author" href="https://nextjs.org" />
   * ```
   */

  keywords?: null | string | Array<string>;
  /**
   * The referrer setting for the document.
   * @example
   * ```tsx
   * "origin"
   * <meta name="referrer" content="origin" />
   * ```
   */

  creator?: null | string;
  /**
   * The publisher of the document.
   * @example
   *
   * ```tsx
   * "Vercel"
   * <meta name="publisher" content="Vercel" />
   * ```
   */
  publisher?: null | string;
  /**
   * The robots setting for the document.
   *
   * @see https://developer.mozilla.org/en-US/docs/Glossary/Robots.txt
   * @example
   * ```tsx
   * "index, follow"
   * <meta name="robots" content="index, follow" />
   *
   * { index: false, follow: false }
   * <meta name="robots" content="noindex, nofollow" />
   * ```
   */

  icons?: null | any;
  /**
   * A web application manifest, as defined in the Web Application Manifest specification.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Manifest
   * @example
   * ```tsx
   * "https://example.com/manifest.json"
   * <link rel="manifest" href="https://example.com/manifest.json" />
   * ```
   *
   */
  manifest?: null | string | URL;
  /**
   * The Open Graph metadata for the document.
   *
   * @see https://ogp.me
   * @example
   * ```tsx
   * {
   *   type: "website",
   *   url: "https://example.com",
   *   title: "My Website",
   *   description: "My Website Description",
   *   siteName: "My Website",
   *   images: [{
   *     url: "https://example.com/og.png",
   *   }],
   * }
   *
   * <meta property="og:type" content="website" />
   * <meta property="og:url" content="https://example.com" />
   * <meta property="og:site_name" content="My Website" />
   * <meta property="og:title" content="My Website" />
   * <meta property="og:description" content="My Website Description" />
   * <meta property="og:image" content="https://example.com/og.png" />
   * ```
   */
  openGraph?: null | any;
  /**
   * The Twitter metadata for the document.
   * @example
   * ```tsx
   * { card: "summary_large_image", site: "@site", creator: "@creator", "images": "https://example.com/og.png" }
   *
   * <meta name="twitter:card" content="summary_large_image" />
   * <meta name="twitter:site" content="@site" />
   * <meta name="twitter:creator" content="@creator" />
   * <meta name="twitter:title" content="My Website" />
   * <meta name="twitter:description" content="My Website Description" />
   * <meta name="twitter:image" content="https://example.com/og.png" />
   * ```
   *
   */
  twitter?: null | any;
  /**
   * The common verification tokens for the document.
   * @example
   * ```tsx
   * { verification: { google: "1234567890", yandex: "1234567890", "me": "1234567890" } }
   * <meta name="google-site-verification" content="1234567890" />
   * <meta name="yandex-verification" content="1234567890" />
   * <meta name="me" content="@me" />
   * ```
   */
  verification?: any;
  /**
   * The Apple web app metadata for the document.
   *
   * @see https://developer.apple.com/library/archive/documentation/AppleApplications/Reference/SafariHTMLRef/Articles/MetaTags.html
   * @example
   * ```tsx
   * { capable: true, title: "My Website", statusBarStyle: "black-translucent" }
   * <meta name="apple-mobile-web-app-capable" content="yes" />
   * <meta name="apple-mobile-web-app-title" content="My Website" />
   * <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
   * ```
   *
   */
  appleWebApp?: null | boolean | any;
  /**
   * Indicates if devices should try to interpret various formats and make actionable links out of them. For example it controles
   * if telephone numbers on mobile that can be clicked to dial or not.
   * @example
   * ```tsx
   * { telephone: false }
   * <meta name="format-detection" content="telephone=no" />
   * ```
   *
   */

  abstract?: null | string;
  /**
   * The Facebook AppLinks metadata for the document.
   * @example
   * ```tsx
   * { ios: { appStoreId: "123456789", url: "https://example.com" }, android: { packageName: "com.example", url: "https://example.com" } }
   *
   * <meta property="al:ios:app_store_id" content="123456789" />
   * <meta property="al:ios:url" content="https://example.com" />
   * <meta property="al:android:package" content="com.example" />
   * <meta property="al:android:url" content="https://example.com" />
   * ```
   */
  appLinks?: null | any;
  /**
   * The archives link rel property.
   * @example
   * ```tsx
   * { archives: "https://example.com/archives" }
   * <link rel="archives" href="https://example.com/archives" />
   * ```
   */
  archives?: null | string | Array<string>;
  /**
   * The assets link rel property.
   * @example
   * ```tsx
   * "https://example.com/assets"
   * <link rel="assets" href="https://example.com/assets" />
   * ```
   */
  assets?: null | string | Array<string>;
  /**
   * The bookmarks link rel property.
   * @example
   * ```tsx
   * "https://example.com/bookmarks"
   * <link rel="bookmarks" href="https://example.com/bookmarks" />
   * ```
   */
  bookmarks?: null | string | Array<string>;
  /**
   * The category meta name property.
   * @example
   * ```tsx
   * "My Category"
   * <meta name="category" content="My Category" />
   * ```
   */
  category?: null | string;
  /**
   * The classification meta name property.
   * @example
   * ```tsx
   * "My Classification"
   * <meta name="classification" content="My Classification" />
   * ```
   */
  classification?: null | string;
  /**
   * Arbitrary name/value pairs for the document.
   */
}
